import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { Mail, Phone, ChevronRight } from 'lucide-react';
import axios from 'axios';
import SEO, { generateBreadcrumbSchema } from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const { t, language } = useLanguage();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await axios.get(`${API}/employees`);
        setEmployees(res.data);
      } catch (error) {
        console.error('Error fetching employees:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEmployees();
  }, []);

  return (
    <div data-testid="contact-page" className="bg-[#FAFAF8] min-h-screen pt-24">
      <SEO 
        title={language === 'sv' 
          ? 'Kontakta oss | Flowingo AB' 
          : 'Contact Us | Flowingo AB'}
        description={language === 'sv'
          ? 'Kontakta Flowingo AB för VVS-produkter och teknisk rådgivning. Vårt erfarna team hjälper dig med offertförfrågningar, teknisk support och leveranser. Ring +46(0)31 27 32 32.'
          : 'Contact Flowingo AB for HVAC products and technical advice. Our experienced team helps you with quote requests, technical support and deliveries. Call +46(0)31 27 32 32.'}
        language={language}
        schema={[
          generateBreadcrumbSchema([
            { name: language === 'sv' ? 'Hem' : 'Home', url: 'https://flowingo.se' },
            { name: language === 'sv' ? 'Kontakt' : 'Contact', url: 'https://flowingo.se/kontakt' },
          ]),
          {
            '@context': 'https://schema.org',
            '@type': 'ContactPage',
            '@id': 'https://flowingo.se/kontakt',
            name: language === 'sv' ? 'Kontakta Flowingo AB' : 'Contact Flowingo AB',
            description: 'Kontaktinformation för Flowingo AB',
            isPartOf: { '@id': 'https://flowingo.se/#website' },
            mainEntity: {
              '@type': 'Organization',
              '@id': 'https://flowingo.se/#organization',
            },
          }
        ]}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <nav className="flex items-center gap-2 text-sm text-stone-500 mb-6">
          <Link to="/" className="hover:text-stone-900">{t('nav.home')}</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-stone-900">{t('nav.contact')}</span>
        </nav>
        <p className="text-stone-500 text-sm uppercase tracking-widest mb-2">
          {language === 'sv' ? 'Vårt team' : 'Our team'}
        </p>
        <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 mb-4">
          {language === 'sv' 
            ? 'Kontakta Flowingo AB – VVS-expertis nära dig' 
            : 'Contact Flowingo AB – HVAC Expertise Near You'}
        </h1>
        <p className="text-stone-600 max-w-2xl">
          {language === 'sv' 
            ? 'Vårt erfarna team står redo att hjälpa dig med teknisk rådgivning, offertförfrågningar och leveranser. Kontakta rätt person direkt för snabbast möjliga service.' 
            : 'Our experienced team is ready to help you with technical advice, quote requests and deliveries. Contact the right person directly for the fastest possible service.'}
        </p>
      </div>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <h2 className="text-2xl font-bold text-stone-900 mb-8">{t('contact.subtitle')}</h2>

        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((n) => (<div key={n} className="bg-white h-80 animate-pulse border border-stone-200" />))}
          </div>
        ) : employees.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-stone-500">{language === 'sv' ? 'Inga medarbetare att visa' : 'No team members to display'}</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {employees.map((employee) => (
              <div key={employee.id} data-testid={`employee-card-${employee.id}`} className="bg-white overflow-hidden border border-stone-200 hover:border-stone-300 transition-colors">
                <div className="aspect-square bg-stone-100 overflow-hidden">
                  {employee.image_url ? (
                    <img 
                      src={employee.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${employee.image_url}` : employee.image_url} 
                      alt={employee.name} 
                      className="w-full h-full object-cover" 
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-stone-400 text-4xl font-bold">{employee.name.charAt(0)}</div>
                  )}
                </div>
                <div className="p-5">
                  <h3 className="text-lg font-semibold text-stone-900 mb-1">{employee.name}</h3>
                  <p className="text-stone-500 text-sm mb-4">{language === 'sv' ? employee.position_sv : employee.position_en}</p>
                  <div className="space-y-2">
                    <a href={`mailto:${employee.email}`} className="flex items-center gap-2 text-stone-600 hover:text-stone-900 text-sm transition-colors">
                      <Mail className="w-4 h-4" /><span className="truncate">{employee.email}</span>
                    </a>
                    <a href={`tel:${employee.phone}`} className="flex items-center gap-2 text-stone-600 hover:text-stone-900 text-sm transition-colors">
                      <Phone className="w-4 h-4" /><span>{employee.phone}</span>
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="bg-white py-20 border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div>
              <h3 className="text-stone-900 font-semibold mb-4">{language === 'sv' ? 'Postadress' : 'Postal Address'}</h3>
              <p className="text-stone-600">
                Flowingo AB<br />
                Solventilsgatan 17D<br />
                417 24 Göteborg<br />
                Sverige
              </p>
            </div>
            <div>
              <h3 className="text-stone-900 font-semibold mb-4">{language === 'sv' ? 'Kontaktuppgifter' : 'Contact Details'}</h3>
              <p className="text-stone-600">
                <a href="tel:+46317273232" className="hover:text-stone-900">+46(0)31 27 32 32</a><br />
                <a href="mailto:info@flowingo.se" className="hover:text-stone-900">info@flowingo.se</a>
              </p>
            </div>
            <div>
              <h3 className="text-stone-900 font-semibold mb-4">{language === 'sv' ? 'Organisationsnummer' : 'Organization Number'}</h3>
              <p className="text-stone-600">559403-7664</p>
            </div>
            <div>
              <h3 className="text-stone-900 font-semibold mb-4">{language === 'sv' ? 'VAT-nummer' : 'VAT Number'}</h3>
              <p className="text-stone-600">SE559403766401</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
